import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import kitService from '../../services/kitService';
import { getApiErrorMessage } from '../../utils/apiError';
import RecetaComponentesGrid, {
  type RecetaComponenteLinea,
} from '../produccion/RecetaComponentesGrid';

const newLinea = (): RecetaComponenteLinea => ({
  id: `kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  codigoArticulo: '',
  descripcion: '',
  unidadMedida: '',
  cantidad: 1,
  costo: 0,
  notas: '',
});

interface Props {
  codigoArticulo: string;
  readOnly?: boolean;
}

const ProductoKitComponentesTab: React.FC<Props> = ({ codigoArticulo, readOnly = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [lineas, setLineas] = useState<RecetaComponenteLinea[]>([newLinea()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [codigoReceta, setCodigoReceta] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!codigoArticulo.trim()) return;
    setLoading(true);
    try {
      const kit = await kitService.getKit(codigoArticulo);
      if (!kit || !kit.componentes?.length) {
        setLineas([newLinea()]);
        setCodigoReceta(null);
        return;
      }
      setCodigoReceta(kit.codigoReceta);
      setLineas(
        kit.componentes.map((c) => ({
          id: `kit-${c.codigoArticulo}-${Math.random().toString(36).slice(2, 6)}`,
          codigoArticulo: c.codigoArticulo,
          descripcion: c.descripcion ?? '',
          unidadMedida: c.unidadMedida ?? '',
          cantidad: Number(c.cantidad) || 1,
          costo: Number(c.costo) || 0,
          notas: c.notas ?? '',
        })),
      );
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar componentes del kit'), {
        variant: 'error',
      });
      setLineas([newLinea()]);
    } finally {
      setLoading(false);
    }
  }, [codigoArticulo, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleLineaChange = (
    id: string,
    field: keyof RecetaComponenteLinea,
    value: string | number,
  ) => {
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const handleLineaFilled = (id: string, data: Partial<RecetaComponenteLinea>) => {
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)));
  };

  const handleEliminar = (id: string) => {
    setLineas((prev) => {
      const next = prev.filter((l) => l.id !== id);
      return next.length === 0 ? [newLinea()] : next;
    });
  };

  const handleGuardar = async () => {
    const validas = lineas.filter((l) => l.codigoArticulo.trim() && l.descripcion.trim());
    if (validas.length === 0) {
      enqueueSnackbar('Agregá al menos un componente con código válido', { variant: 'warning' });
      return;
    }
    if (validas.some((l) => !l.cantidad || l.cantidad <= 0)) {
      enqueueSnackbar('Las cantidades deben ser mayores a cero', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const saved = await kitService.upsertKit(
        codigoArticulo,
        validas.map((l) => ({
          codigoArticulo: l.codigoArticulo.trim(),
          descripcion: l.descripcion.trim(),
          cantidad: Number(l.cantidad),
          notas: (l.notas ?? '').trim() || null,
        })),
      );
      setCodigoReceta(saved.codigoReceta);
      enqueueSnackbar('Componentes del kit guardados', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al guardar componentes del kit'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        Definí los artículos que componen este kit. El stock disponible se calcula según la
        disponibilidad de sus componentes
        {codigoReceta ? ` (receta: ${codigoReceta.trim()})` : ''}.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        Tipos permitidos: Materia Prima, Re-Venta, Venta Directa, Producto Terminado, Servicio o
        otro Kit.
      </Typography>
      <RecetaComponentesGrid
        lineas={lineas}
        onLineaChange={handleLineaChange}
        onLineaFilled={handleLineaFilled}
        onEliminarLinea={handleEliminar}
        onAgregarLineaVacia={() => setLineas((prev) => [...prev, newLinea()])}
        readOnly={readOnly || saving}
        tiposArticulo={kitService.tiposComponentesPermitidos}
        mensajeTipoInvalido="El componente debe ser MP, RV, VD, PT, SER o KIT"
      />
      {!readOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleGuardar()}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Guardar componentes
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default ProductoKitComponentesTab;
